import React, { useEffect, useState } from "react";
import { Form, Select, Table, Button, message, Row, Col, Typography, Empty, Space, Tag } from "antd";
import { EditOutlined, SaveOutlined, CloseOutlined } from "@ant-design/icons";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { getDarkStores, getCities } from "store/admin-slice/adminServicesSlice";
import {
  getPidgeSlots,
  getPidgeStoreSlotConfig,
  updatePidgeStoreSlotConfig,
  removePidgeSlotConfig,
} from "store/pidge/pidgeSlice";

const MESSAGE_KEY = "pidge-slot-config";
const { Text } = Typography;

interface IStoreSlotRow {
  storeId: string;
  slotIds: number[];
}

const PidgeSlotConfigsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  const [cityId, setCityId] = useState<string>();
  const [storeId, setStoreId] = useState<string>();
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSlotIds, setEditedSlotIds] = useState<number[]>([]);

  const { pidgeSlots, storeSlotConfig, slotConfigStatus, slotConfigError } = useAppSelector(
    (state) => state.pidge
  );
  const { darkStores, cities } = useAppSelector((state) => state.adminServices);

  useEffect(() => {
    dispatch(getDarkStores());
    dispatch(getCities());
    dispatch(getPidgeSlots());
    return () => {
      dispatch(removePidgeSlotConfig());
    };
  }, []);

  useEffect(() => {
    if (slotConfigStatus === "loading" || slotConfigStatus === "saving") {
      message.loading({ content: "Loading...", key: MESSAGE_KEY });
    } else if (slotConfigStatus === "success" && hasAttemptedFetch) {
      message.success({ content: "Success!", key: MESSAGE_KEY, duration: 2 });
    } else if (slotConfigStatus === "error" && hasAttemptedFetch) {
      message.error({ content: slotConfigError, key: MESSAGE_KEY, duration: 3 });
    }
    return () => {
      message.destroy(MESSAGE_KEY);
    };
  }, [slotConfigStatus, slotConfigError, hasAttemptedFetch]);

  useEffect(() => {
    if (storeId != undefined) {
      setHasAttemptedFetch(true);
      setIsEditing(false);
      dispatch(getPidgeStoreSlotConfig());
    }
  }, [storeId]);

  const currentSlotIds: number[] =
    storeId && storeSlotConfig ? storeSlotConfig[storeId] ?? [] : [];

  const startEditing = () => {
    setEditedSlotIds(currentSlotIds);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditedSlotIds([]);
  };

  const saveSlotConfig = async () => {
    if (!storeId) return;
    try {
      await dispatch(
        updatePidgeStoreSlotConfig({
          storeId: parseInt(storeId, 10),
          slotIds: editedSlotIds,
        })
      ).unwrap();
      setIsEditing(false);
      // Refresh from server; city/store selection stays intact.
      dispatch(getPidgeStoreSlotConfig());
    } catch (_) {
      // Error toast is handled from slice status changes.
    }
  };

  const columns = [
    {
      title: "Store ID",
      dataIndex: "storeId",
      key: "storeId",
      width: 120,
    },
    {
      title: "Slots",
      dataIndex: "slotIds",
      key: "slotIds",
      render: (slotIds: number[]) =>
        isEditing ? (
          <Select
            mode="multiple"
            style={{ width: "100%", minWidth: 240 }}
            placeholder="Select slots"
            value={editedSlotIds}
            onChange={(value: number[]) => setEditedSlotIds(value)}
            options={pidgeSlots.map((slot) => ({
              value: slot,
              label: `Slot Id : ${slot}`,
            }))}
          />
        ) : slotIds.length ? (
          slotIds.map((slot) => <Tag key={slot}>Slot Id : {slot}</Tag>)
        ) : (
          <Text type="secondary">No slots configured</Text>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right" as const,
      width: 180,
      render: () =>
        isEditing ? (
          <Space>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              size="small"
              loading={slotConfigStatus === "saving"}
              onClick={saveSlotConfig}
            >
              Save
            </Button>
            <Button icon={<CloseOutlined />} size="small" onClick={cancelEditing}>
              Cancel
            </Button>
          </Space>
        ) : (
          <Button icon={<EditOutlined />} size="small" onClick={startEditing}>
            Edit
          </Button>
        ),
    },
  ];

  const getTableData = (): IStoreSlotRow[] => {
    if (!storeId || !storeSlotConfig) return [];
    return [{ storeId, slotIds: currentSlotIds }];
  };

  const renderContent = () => {
    if (!storeId || !hasAttemptedFetch) {
      return null;
    }

    if (!storeSlotConfig) {
      if (slotConfigStatus === "error") {
        return (
          <Row justify="center" align="middle" style={{ minHeight: "400px" }}>
            <Col>
              <Empty
                description={
                  <Text type="secondary" style={{ fontSize: 16 }}>
                    No Slot Configs Found
                  </Text>
                }
              />
            </Col>
          </Row>
        );
      }
      return null; // Initial load in progress.
    }

    return (
      <Row>
        <Col xs={24}>
          <Table
            columns={columns}
            dataSource={getTableData()}
            pagination={false}
            bordered
            size="small"
            loading={slotConfigStatus === "loading"}
            rowKey="storeId"
          />
        </Col>
      </Row>
    );
  };

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Pidge Slot Configs" />
        </Col>
      </Row>
      <Form form={form} layout="vertical" className="mb-8">
        <Row gutter={24}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="City" name="cityId" className="mb-4">
              <Select
                showSearch
                placeholder="Select city"
                className="w-full"
                filterOption={(input, option) => {
                  return option?.children?.toString()
                    .toLowerCase()
                    .includes(input?.toLowerCase() ?? "") ?? false;
                }}
                onChange={(e) => {
                  setCityId(e);
                  setStoreId(undefined);
                  setHasAttemptedFetch(false);
                  setIsEditing(false);
                  form.setFieldsValue({ storeId: undefined });
                  dispatch(removePidgeSlotConfig());
                }}
              >
                {(cities ?? []).map((city) => (
                  <Select.Option value={city.id} key={city.id}>
                    {city.name} || {city.id}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {cityId && (
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item label="Store ID" name="storeId" className="mb-4">
                <Select
                  showSearch
                  placeholder="Select store"
                  className="w-full"
                  value={storeId}
                  filterOption={(input, option) => {
                    return option?.children?.toString()
                      .toLowerCase()
                      .includes(input?.toLowerCase() ?? "") ?? false;
                  }}
                  onChange={(e) => {
                    setStoreId(e?.toString());
                  }}
                >
                  {(darkStores ?? [])
                    .filter((store) => store?.cityId?.toString() == cityId)
                    .map((storeInfo) => (
                      <Select.Option value={storeInfo.id} key={storeInfo.id}>
                        {storeInfo.name} || {storeInfo.id}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>

      {renderContent()}
    </>
  );
};

export default PidgeSlotConfigsPage;
